import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import AdminLayout from '../../components/AdminLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { Save, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminSettings() {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await adminAPI.settings();
      setSettings(res.data);
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await adminAPI.updateSettings(settings);
      toast.success('Settings saved');
    } catch { toast.error('Save failed'); } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="w-8 h-8 border-2 border-[#FF7A3D] border-t-transparent rounded-full animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div data-testid="admin-settings-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="font-mono text-xs text-[#ff003c] tracking-widest uppercase mb-2">PLATFORM CONFIG</p>
            <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Settings</h1>
          </div>
          <div className="flex gap-2">
            <Button onClick={load} variant="outline" className="border-white/10 text-[#888] font-heading text-[10px] uppercase rounded-sm h-9 bg-transparent" data-testid="refresh-settings">
              <RefreshCw className="w-3 h-3 mr-1" /> RELOAD
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#FF7A3D] text-black font-heading text-[10px] uppercase rounded-sm h-9" data-testid="save-settings">
              <Save className="w-3 h-3 mr-1" /> {saving ? 'SAVING...' : 'SAVE ALL'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          {/* Credits */}
          <div className="card-cyber rounded-sm p-6" data-testid="credits-settings">
            <h2 className="font-heading text-lg font-bold text-white mb-4">CREDIT SYSTEM</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="font-mono text-[10px] text-[#aaa] tracking-widest">NEW USER CREDITS</Label>
                <Input
                  type="number" step="0.5"
                  value={settings.new_user_credits || 5}
                  onChange={(e) => setSettings({...settings, new_user_credits: parseFloat(e.target.value)})}
                  className="bg-black border-white/10 font-mono text-xs mt-1"
                  data-testid="new-user-credits-input"
                />
                <p className="font-mono text-[10px] text-[#888] mt-1">Credits given to new registrations</p>
              </div>
              <div>
                <Label className="font-mono text-[10px] text-[#aaa] tracking-widest">MAX EXECUTIONS / DAY</Label>
                <Input
                  type="number"
                  value={settings.max_executions_per_day || 100}
                  onChange={(e) => setSettings({...settings, max_executions_per_day: parseInt(e.target.value)})}
                  className="bg-black border-white/10 font-mono text-xs mt-1"
                  data-testid="max-executions-input"
                />
                <p className="font-mono text-[10px] text-[#888] mt-1">Rate limit per user per day</p>
              </div>
            </div>
          </div>

          {/* Auth */}
          <div className="card-cyber rounded-sm p-6" data-testid="auth-settings">
            <h2 className="font-heading text-lg font-bold text-white mb-4">AUTHENTICATION</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-white">Google OAuth</p>
                  <p className="font-mono text-[10px] text-[#aaa]">Allow login via Google</p>
                </div>
                <Switch
                  checked={settings.google_oauth_enabled ?? true}
                  onCheckedChange={(v) => setSettings({...settings, google_oauth_enabled: v})}
                  data-testid="toggle-google-auth"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-mono text-xs text-white">GitHub OAuth</p>
                  <p className="font-mono text-[10px] text-[#aaa]">Allow login via GitHub (requires GITHUB_CLIENT_ID env)</p>
                </div>
                <Switch
                  checked={settings.github_oauth_enabled ?? false}
                  onCheckedChange={(v) => setSettings({...settings, github_oauth_enabled: v})}
                  data-testid="toggle-github-auth"
                />
              </div>
            </div>
          </div>

          {/* Announcement */}
          <div className="card-cyber rounded-sm p-6" data-testid="announcement-settings">
            <h2 className="font-heading text-lg font-bold text-white mb-4">PLATFORM ANNOUNCEMENT</h2>
            <textarea
              value={settings.platform_announcement || ''}
              onChange={(e) => setSettings({...settings, platform_announcement: e.target.value})}
              rows={3}
              className="w-full bg-black border border-white/10 rounded-sm p-3 font-mono text-xs text-white resize-y focus:border-[#FF7A3D] focus:outline-none placeholder:text-[#555]"
              placeholder="Enter a platform-wide announcement (leave empty to hide)..."
              data-testid="announcement-input"
            />
          </div>

          {/* Maintenance */}
          <div className="card-cyber rounded-sm p-6" data-testid="maintenance-settings">
            <h2 className="font-heading text-lg font-bold text-white mb-4">MAINTENANCE</h2>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-xs text-white">Maintenance Mode</p>
                <p className="font-mono text-[10px] text-[#ff003c]">WARNING: This will block all non-admin access to the platform</p>
              </div>
              <Switch
                checked={settings.maintenance_mode ?? false}
                onCheckedChange={(v) => setSettings({...settings, maintenance_mode: v})}
                data-testid="toggle-maintenance"
              />
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
